import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Plus, Search, Pin, Lock, X, Eye, Clock, Ban } from "lucide-react";
import { Button, EmptyState } from "../ui";
import BlockedMembersDialog from "./BlockedMembersDialog";
import {
  discussionsApi,
  errMessage,
  formatDateTime,
  formatUnread,
} from "./api";
import NewTopicDialog from "./NewTopicDialog";
import "./DiscussionsPage.css";

const LIST_POLL_MS = 30000;

export default function DiscussionsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent"); // recent | most_commented | most_viewed
  const [newOpen, setNewOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(
    (opts = {}) => {
      const { silent } = opts;
      if (!silent) setItems(null);
      discussionsApi
        .listTopics({ q: search || undefined, sort })
        .then((d) => {
          setItems(d.items || []);
          setNextBefore(d.next_before || null);
          setError("");
        })
        .catch((e) => {
          if (!silent) {
            setItems([]);
            setError(errMessage(e));
          }
        });
    },
    [search, sort]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Light refresh on focus + 30s while focused (spec §10 list cadence).
  useEffect(() => {
    const tick = () => load({ silent: true });
    pollRef.current = setInterval(tick, LIST_POLL_MS);
    const onFocus = () => load({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(q.trim());
  };

  const clearSearch = () => {
    setQ("");
    setSearch("");
  };

  const loadMore = () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    discussionsApi
      .listTopics({ q: search || undefined, sort, before: nextBefore })
      .then((d) => {
        setItems((prev) => [...(prev || []), ...(d.items || [])]);
        setNextBefore(d.next_before || null);
      })
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoadingMore(false));
  };

  const pinned = (items || []).filter((t) => t.is_pinned);
  const rest = (items || []).filter((t) => !t.is_pinned);

  return (
    <div className="vdsc-shell">
      <div className="vdsc-head">
        <div>
          <h1>
            <MessageSquare size={22} /> Discussions
          </h1>
          <p className="vdsc-sub">
            The community town square — start a topic, share photos, quote each
            other and chat in real time.
          </p>
        </div>
        <div className="vdsc-head-actions">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Ban size={14} />}
            onClick={() => setBlockedOpen(true)}
          >
            Blocked members
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setNewOpen(true)}
          >
            New topic
          </Button>
        </div>
      </div>

      <form className="vdsc-searchbar" onSubmit={submitSearch} role="search">
        <Search size={16} className="vdsc-search-icon" />
        <input
          className="vdsc-search-input"
          type="search"
          placeholder="Search topics…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search topics"
        />
        {search && (
          <button
            type="button"
            className="vdsc-search-clear"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <X size={15} />
          </button>
        )}
      </form>

      <div className="vdsc-filterbar" role="tablist" aria-label="Sort topics">
        {[
          { key: "recent", label: "Recent" },
          { key: "most_commented", label: "Most commented" },
          { key: "most_viewed", label: "Most viewed" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={sort === opt.key}
            className={`vdsc-filter ${sort === opt.key ? "active" : ""}`}
            onClick={() => setSort(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && <div className="vdsc-error">{error}</div>}
      {items === null && <div className="vdsc-loading">Loading topics…</div>}

      {items !== null && items.length === 0 && !error && (
        <EmptyState
          icon={<MessageSquare size={28} strokeWidth={1.4} />}
          title={search ? "No topics match your search" : "No topics yet"}
          subtitle={
            search
              ? "Try a different search, or start the conversation yourself."
              : "Be the first to start a conversation with the community."
          }
          cta={
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              New topic
            </Button>
          }
        />
      )}

      {pinned.length > 0 && (
        <section className="vdsc-section">
          <h2 className="vdsc-section-title">
            <Pin size={13} /> Pinned
          </h2>
          <div className="vdsc-list">
            {pinned.map((t) => (
              <TopicRow key={t.id} topic={t} navigate={navigate} />
            ))}
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="vdsc-section">
          {pinned.length > 0 && (
            <h2 className="vdsc-section-title">All topics</h2>
          )}
          <div className="vdsc-list">
            {rest.map((t) => (
              <TopicRow key={t.id} topic={t} navigate={navigate} />
            ))}
          </div>
        </section>
      )}

      {nextBefore && (
        <div className="vdsc-loadmore">
          <Button variant="secondary" size="sm" onClick={loadMore} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}

      <NewTopicDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(topic) => {
          if (topic?.id) navigate(`/discussions/topics/${topic.id}`);
        }}
      />
      <BlockedMembersDialog open={blockedOpen} onOpenChange={setBlockedOpen} />
    </div>
  );
}

function TopicRow({ topic }) {
  const badge = formatUnread(topic.unread_count);
  const closed = topic.status === "CLOSED";
  const comments = topic.message_count || 0;
  const views = topic.view_count || 0;
  const n = (v) => v.toLocaleString("en-IN");
  return (
    <Link to={`/discussions/topics/${topic.id}`} className="vdsc-topicrow">
      <div className="vdsc-topicrow-main">
        <div className="vdsc-topicrow-titleline">
          {topic.is_pinned && <Pin size={13} className="vdsc-topicrow-pin" />}
          <span className="vdsc-topicrow-title">{topic.title}</span>
          {closed && (
            <span className="vdsc-topicrow-closed">
              <Lock size={11} /> Closed
            </span>
          )}
        </div>
        {topic.preview ? (
          <span className="vdsc-topicrow-preview">{topic.preview}</span>
        ) : (
          <span className="vdsc-topicrow-preview muted">No messages yet</span>
        )}
        <div className="vdsc-topicrow-stats">
          <span title={`${comments} ${comments === 1 ? "comment" : "comments"}`}>
            <MessageSquare size={12} /> {n(comments)}
          </span>
          <span title={`${views} ${views === 1 ? "view" : "views"}`}>
            <Eye size={12} /> {n(views)}
          </span>
          {topic.last_message_at && (
            <span title="Last comment">
              <Clock size={12} /> {formatDateTime(topic.last_message_at)}
            </span>
          )}
        </div>
      </div>
      <div className="vdsc-topicrow-side">
        {badge && <span className="vdsc-badge">{badge}</span>}
      </div>
    </Link>
  );
}
